import { useRef, useCallback } from 'react';
import L from 'leaflet';
import { polygonAreaSF, polylineLengthFeet } from '../utils/pixelMeasurements';

/**
 * Drawing hook for Plan Takeoff maps operating in CRS.Simple pixel space.
 *
 * Unlike useAreaDrawing in SiteTakeoffV3, this hook:
 * - Uses pixel coordinates (not lat/lng) for all measurements.
 * - Uses Shoelace formula for polygon area and Euclidean distance for polyline length.
 * - Has no snapping (no leaflet-snap).
 * - Calls onItemDrawn with { geometryType, pixelPaths, measuredQty, measuredUnit }.
 *
 * In CRS.Simple, Leaflet internally stores points using [lat=y, lng=x], so
 * getLatLng()/getLatLngs() return { lat: pixelY, lng: pixelX }.
 * We normalize these to { x, y } pixel objects before passing to callers.
 *
 * @param {Object} options
 * @param {React.MutableRefObject} options.mapRef - The Leaflet map instance ref.
 * @param {React.MutableRefObject} options.drawnItemsRef - The drawn items FeatureGroup ref.
 * @param {number} options.pixelsPerFoot - Calibration scale factor. 0 = uncalibrated.
 * @param {function} options.onItemDrawn - Callback fired after a shape is completed.
 */
export default function usePlanDrawing({ mapRef, drawnItemsRef, pixelsPerFoot = 0, onItemDrawn }) {
  const handlerRef = useRef(null);

  /**
   * Convert a Leaflet LatLng (in CRS.Simple pixel space) to a { x, y } pixel object.
   * In CRS.Simple: latLng.lat === pixelY, latLng.lng === pixelX.
   */
  const latLngToPixel = (ll) => ({ x: ll.lng, y: ll.lat });

  const startDraw = useCallback((tool) => {
    const map = mapRef.current;
    if (!map) return;

    // Disable any active handler first
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }

    let handler;
    switch (tool) {
      case 'polygon':
        handler = new L.Draw.Polygon(map, {
          shapeOptions: {
            color: '#1976d2',
            weight: 2,
            fillOpacity: 0,
            opacity: 0.9,
          },
          allowIntersection: false,
          showArea: false, // We compute our own area
        });
        break;

      case 'polyline':
      case 'line':
        handler = new L.Draw.Polyline(map, {
          shapeOptions: { color: '#1976d2', weight: 3 },
        });
        break;

      case 'point':
      case 'marker':
        handler = new L.Draw.Marker(map, {});
        break;

      default:
        return;
    }

    // One-shot listener — removes itself after the first shape is completed
    const listener = (e) => {
      map.off(L.Draw.Event.CREATED, listener);
      handlerRef.current = null;

      const layer = e.layer;
      const isMarker = layer instanceof L.Marker;
      const isPolyline = (layer instanceof L.Polyline) && !(layer instanceof L.Polygon);

      let result;

      if (isMarker) {
        const ll = layer.getLatLng();
        const pixel = latLngToPixel(ll);
        result = {
          geometryType: 'point',
          pixelPaths: [pixel],
          measuredQty: 1,
          measuredUnit: 'ea',
        };
      } else if (isPolyline) {
        const latlngs = layer.getLatLngs();
        const pixelPaths = latlngs.map(latLngToPixel);
        const lengthFt = polylineLengthFeet(pixelPaths, pixelsPerFoot);
        result = {
          geometryType: 'polyline',
          pixelPaths,
          measuredQty: parseFloat(lengthFt.toFixed(2)),
          measuredUnit: pixelsPerFoot ? 'lf' : 'px',
        };
      } else {
        // polygon
        const latlngs = layer.getLatLngs();
        // L.Draw.Polygon may return nested arrays for complex polygons; flatten one level
        const flat = Array.isArray(latlngs[0]) ? latlngs[0] : latlngs;
        const pixelPaths = flat.map(latLngToPixel);
        const areaSF = polygonAreaSF(pixelPaths, pixelsPerFoot);
        result = {
          geometryType: 'polygon',
          pixelPaths,
          measuredQty: parseFloat(areaSF.toFixed(2)),
          measuredUnit: pixelsPerFoot ? 'sf' : 'px²',
        };
      }

      // Remove the temporary draw layer — the caller is responsible for rendering
      // the permanent styled version via drawnItemsRef or their own state.
      try {
        if (drawnItemsRef.current) {
          drawnItemsRef.current.removeLayer(layer);
        }
      } catch (_) {}

      if (onItemDrawn) onItemDrawn(result);
    };

    map.on(L.Draw.Event.CREATED, listener);
    handler.enable();
    handlerRef.current = handler;
  }, [mapRef, drawnItemsRef, pixelsPerFoot, onItemDrawn]);

  const cancelDraw = useCallback(() => {
    if (handlerRef.current?.disable) {
      try { handlerRef.current.disable(); } catch (_) {}
      handlerRef.current = null;
    }
  }, []);

  return { startDraw, cancelDraw };
}
